import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import { ethers } from 'ethers6';
import { useAccount, useBalance } from 'wagmi';
import { TokenData } from '../../../types';
import { config } from '../../../utils/config';
import { useVestingSchedules } from '../../../hooks/useVestingSchedules';

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
`;

const ModalContent = styled.div`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  backdrop-filter: blur(8px);
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  text-align: center;
`;

const SectionLabel = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AmountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const AmountInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  padding: 12px 16px;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TokenDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 16px 20px;
  min-width: 120px;
`;

const TokenLogo = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const TokenSymbol = styled.div`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
`;

const TokenName = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const BalanceText = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
`;

const PercentButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 16px;
`;

const PercentButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(168, 85, 247, 0.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const VestingOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
`;

const VestingOptionButton = styled.button<{ selected: boolean }>`
  background: ${props => props.selected ? 'rgba(168, 85, 247, 0.2)' : 'transparent'};
  border: none;
  border-radius: 8px;
  color: ${props => props.selected ? '#ffffff' : 'rgba(255, 255, 255, 0.8)'};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: ${props => props.selected ? '600' : '500'};
  padding: 16px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(168, 85, 247, 0.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OptionLabel = styled.div`
  font-size: 16px;
  margin-bottom: 4px;
`;

const OptionSubtext = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

const VestButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(168, 85, 247, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: rgba(80, 80, 90, 0.6);
    cursor: not-allowed;
    transform: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 24px;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: #ffffff;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  margin-top: 8px;
  padding: 8px;
  background: transparent;
  border-radius: 6px;
`;

const SuccessMessage = styled.div`
  color: #10b981;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  margin-top: 8px;
  padding: 8px;
  background: transparent;
  border-radius: 6px;
  text-align: center;
`;

const TxLink = styled.a`
  color: #10b981;
  text-decoration: underline;
  margin-left: 4px;
`;

const SearchInput = styled.input`
  width: 100%;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  padding: 12px 16px;
  margin-bottom: 12px;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const TokenList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
`;

const VESTING_CONTRACT_ADDRESS = config.ink.vestingContractAddress;

const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function approve(address,uint256) returns (bool)',
  'function allowance(address,address) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function transfer(address,uint256) returns (bool)'
];

const VESTING_ABI = [
  {
    "inputs": [
      {"internalType": "address", "name": "_tokenAddress", "type": "address"},
      {"internalType": "uint256", "name": "_tokenAmount", "type": "uint256"},
      {"internalType": "uint256", "name": "_vestingOption", "type": "uint256"}
    ],
    "name": "createVestingSchedule",
    "outputs": [{"internalType": "uint256", "name": "vestingId", "type": "uint256"}],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [{"internalType": "uint256", "name": "_vestingId", "type": "uint256"}],
    "name": "claimVestedTokens",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

interface QuickVestModalProps {
  isOpen: boolean;
  onClose: () => void;
  userTokens: TokenData[];
  onVestingCreated?: () => void;
}

interface VestingOption {
  value: number;
  days: string;
  label: string;
}

const VESTING_OPTIONS: VestingOption[] = [
  { value: 1, days: '30', label: '1 Month' },
  { value: 2, days: '60', label: '2 Months' },
  { value: 3, days: '90', label: '3 Months' },
  { value: 4, days: '180', label: '6 Months' }
];

const QuickVestModal: React.FC<QuickVestModalProps> = ({ 
  isOpen, 
  onClose, 
  userTokens,
  onVestingCreated 
}) => {
  const { address, isConnected } = useAccount();
  const [selectedToken, setSelectedToken] = useState<TokenData | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [balance, setBalance] = useState('0');
  const [decimals, setDecimals] = useState(18);
  const [allowance, setAllowance] = useState('0');
  const [isApproving, setIsApproving] = useState(false);
  const [isVesting, setIsVesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [showActiveVestings, setShowActiveVestings] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimingId, setClaimingId] = useState<bigint | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter tokens based on search query
  const filteredTokens = userTokens.filter(token => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      token.name?.toLowerCase().includes(query) ||
      token.symbol?.toLowerCase().includes(query) ||
      token.contract_address?.toLowerCase().includes(query)
    );
  });

  // Reset all state when modal closes (so it's fresh on next open)
  useEffect(() => {
    if (!isOpen) {
      setSelectedToken(null);
      setSelectedOption(null);
      setAmount('');
      setBalance('0');
      setDecimals(18);
      setAllowance('0');
      setIsApproving(false);
      setIsVesting(false);
      setError(null);
      setSuccess(false);
      setTxHash(null);
      setShowActiveVestings(false);
      setIsClaiming(false);
      setClaimingId(null);
      setSearchQuery('');
    }
  }, [isOpen]);
  
  // Fetch vesting schedules for the selected token
  const tokenAddress = selectedToken?.contract_address || null;
  const { schedules, isLoading: isLoadingSchedules, refetch: refetchSchedules } = useVestingSchedules(tokenAddress || '');

  // Fetch balance and allowance when token is selected
  useEffect(() => {
    if (!selectedToken || !address || !isConnected) return;

    const fetchTokenData = async () => {
      if (!window.ethereum) return;
      try {
        // Use Gelato RPC for reads
        const gelatoProvider = new ethers.JsonRpcProvider('https://rpc-gel.inkonchain.com/976f88ff75f248cd8fe57b7634a5fb9c');
        const tokenContract = new ethers.Contract(selectedToken.contract_address, ERC20_ABI, gelatoProvider);
        
        const [balanceResult, decimalsResult, allowanceResult] = await Promise.all([
          tokenContract.balanceOf(address),
          tokenContract.decimals(),
          tokenContract.allowance(address, VESTING_CONTRACT_ADDRESS)
        ]);

        setBalance(ethers.formatUnits(balanceResult, decimalsResult));
        setDecimals(Number(decimalsResult));
        setAllowance(ethers.formatUnits(allowanceResult, decimalsResult));
      } catch (err) {
        console.error('Error fetching token data:', err);
        setError('Failed to fetch token data');
      }
    };

    fetchTokenData();
  }, [selectedToken, address, isConnected]);

  const needsApproval = parseFloat(allowance) < parseFloat(amount || '0');

  const handleApprove = async () => {
    if (!selectedToken || !amount || !window.ethereum) return;

    setIsApproving(true);
    setError(null);

    try {
      const walletProvider = new ethers.BrowserProvider(window.ethereum);
      const signer = await walletProvider.getSigner();
      const tokenContract = new ethers.Contract(selectedToken.contract_address, ERC20_ABI, signer);
      
      // Approve maximum amount (like locker)
      const MAX_UINT = '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';
      const tx = await tokenContract.approve(VESTING_CONTRACT_ADDRESS, MAX_UINT);
      await tx.wait();
      
      setAllowance('999999999'); // Set to large number to bypass approval checks
      console.log('Approval successful');
    } catch (err: any) {
      console.error('Approval failed:', err);
      if (err?.code === 4001) {
        setError('User rejected');
      } else {
        setError('Approval failed. Please try again.');
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleVest = async () => {
    if (!selectedToken || !amount || selectedOption === null || !window.ethereum) return;

    // Double-check approval before attempting vesting
    if (parseFloat(allowance) < parseFloat(amount)) {
      setError('Please approve tokens first. The "Approve Token" button should appear above.');
      return;
    }

    setIsVesting(true);
    setError(null);

    try {
      const walletProvider = new ethers.BrowserProvider(window.ethereum);
      const signer = await walletProvider.getSigner();
      const vestingContract = new ethers.Contract(VESTING_CONTRACT_ADDRESS, VESTING_ABI, signer);
      
      // Convert amount to full decimals (wei)
      const amountInWei = ethers.parseUnits(amount, decimals);
      
      // Call createVestingSchedule
      const tx = await vestingContract.createVestingSchedule(
        selectedToken.contract_address,
        amountInWei,
        selectedOption
      );
      
      const receipt = await tx.wait();
      setTxHash(receipt.transactionHash);
      setSuccess(true);
      onVestingCreated?.();
      refetchSchedules(); // Refresh the vesting schedules list
      console.log('Vesting schedule created successfully');
    } catch (err: any) {
      console.error('Vesting failed:', err);
      if (err?.code === 4001) {
        setError('User rejected');
      } else {
        let errorMsg = 'Vesting failed. Please try again.';
        const errorString = JSON.stringify(err);
        if (errorString.includes('ERC20: transfer amount exceeds allowance') || errorString.includes('insufficient allowance')) {
          errorMsg = 'Please approve tokens first by clicking "Approve Token" button.';
        } else if (err?.message?.includes('insufficient token balance')) {
          errorMsg = 'Insufficient token balance.';
        }
        setError(errorMsg);
      }
    } finally {
      setIsVesting(false);
    }
  };

  const handleClaim = async (vestingId: bigint) => {
    if (!address || !window.ethereum) return;

    setIsClaiming(true);
    setClaimingId(vestingId);

    try {
      const walletProvider = new ethers.BrowserProvider(window.ethereum);
      const signer = await walletProvider.getSigner();
      const vestingContract = new ethers.Contract(VESTING_CONTRACT_ADDRESS, VESTING_ABI, signer);
      
      const tx = await vestingContract.claimVestedTokens(vestingId);
      await tx.wait();
      
      console.log('Claim successful');
      refetchSchedules();
      alert('Tokens claimed successfully!');
    } catch (err: any) {
      console.error('Claim failed:', err);
      if (err?.code === 4001) {
        alert('User rejected');
      } else {
        alert('Claim failed. Please try again.');
      }
    } finally {
      setIsClaiming(false);
      setClaimingId(null);
    }
  };

  const formatAmount = (amount: bigint) => {
    return parseFloat(ethers.formatUnits(amount, decimals)).toFixed(2);
  };

  const formatDate = (timestamp: bigint) => {
    const date = new Date(Number(timestamp) * 1000);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const setPercent = (percent: number) => {
    if (!balance) return;
    const newAmount = (parseFloat(balance) * percent).toFixed(6);
    setAmount(newAmount);
  };

  const canVest = selectedToken && selectedOption !== null && amount && parseFloat(amount) > 0 && !needsApproval;

  return (
    <Modal 
      isOpen={isOpen} 
      onRequestClose={onClose}
      style={{
        overlay: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
        },
        content: {
          position: 'relative',
          inset: 'auto',
          background: 'transparent',
          border: 'none',
          padding: 0,
          maxHeight: '90vh',
          maxWidth: '90%',
          width: '100%',
        },
      }}
    >
      <ModalContainer>
        <ModalContent>
          <CloseButton onClick={onClose}>×</CloseButton>
          
          <Title>Vest Tokens</Title>

          {!selectedToken ? (
            <>
              <SectionLabel>Select Token</SectionLabel>
              <SearchInput
                type="text"
                placeholder="Search by name, symbol, or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <TokenList>
                {filteredTokens.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    color: 'rgba(255, 255, 255, 0.5)', 
                    padding: '20px',
                    fontSize: '14px'
                  }}>
                    No tokens found
                  </div>
                ) : (
                  filteredTokens.map((token) => (
                    <TokenDisplay 
                      key={token.contract_address}
                      onClick={() => setSelectedToken(token)}
                      style={{ cursor: 'pointer', marginBottom: '8px' }}
                    >
                      {token.logo_url && (
                        <TokenLogo 
                          src={token.logo_url} 
                          alt={token.symbol}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/default-token.png';
                          }}
                        />
                      )}
                      <TokenInfo>
                        <TokenSymbol>{token.symbol}</TokenSymbol>
                        <TokenName>{token.name}</TokenName>
                      </TokenInfo>
                    </TokenDisplay>
                  ))
                )}
              </TokenList>
            </>
          ) : (
            <>
              <SectionLabel>Selected Token</SectionLabel>
              <TokenDisplay>
                {selectedToken.logo_url && (
                  <TokenLogo 
                    src={selectedToken.logo_url} 
                    alt={selectedToken.symbol}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/default-token.png';
                    }}
                  />
                )}
                <TokenInfo>
                  <TokenSymbol>{selectedToken.symbol}</TokenSymbol>
                  <TokenName>{selectedToken.name}</TokenName>
                </TokenInfo>
              </TokenDisplay>

              <BalanceRow>
                <BalanceText>Balance: {parseFloat(balance).toFixed(6)} {selectedToken.symbol}</BalanceText>
              </BalanceRow>

              <AmountRow>
                <AmountInput
                  type="number"
                  placeholder="0.0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={isVesting}
                />
              </AmountRow>

              <PercentButtons>
                <PercentButton onClick={() => setPercent(0.25)} disabled={isVesting}>25%</PercentButton>
                <PercentButton onClick={() => setPercent(0.5)} disabled={isVesting}>50%</PercentButton>
                <PercentButton onClick={() => setPercent(0.75)} disabled={isVesting}>75%</PercentButton>
                <PercentButton onClick={() => setPercent(1)} disabled={isVesting}>Max</PercentButton>
              </PercentButtons>

              <SectionLabel>Vesting Duration</SectionLabel>
              <VestingOptions>
                {VESTING_OPTIONS.map((option) => (
                  <VestingOptionButton
                    key={option.value}
                    selected={selectedOption === option.value}
                    onClick={() => setSelectedOption(option.value)}
                    disabled={isVesting}
                  >
                    <OptionLabel>{option.label}</OptionLabel>
                    <OptionSubtext>{option.days} days</OptionSubtext>
                  </VestingOptionButton>
                ))}
              </VestingOptions>

              {/* Active Vesting Schedules */}
              {schedules.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <SectionLabel>Active Vesting Schedules</SectionLabel>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px' }}>
                    {schedules.map((schedule) => {
                      const canClaim = schedule.claimableAmount > 0n;
                      return (
                        <div key={schedule.vestingId.toString()} style={{ 
                          background: 'transparent', 
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '8px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Vesting #{schedule.vestingId.toString()}</span>
                            <span style={{ fontSize: '12px', color: canClaim ? '#22c55e' : 'rgba(255,255,255,0.7)' }}>
                              {canClaim ? 'Claimable' : 'Locked'}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
                            Total: {formatAmount(schedule.totalAmount)} {selectedToken.symbol}
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
                            Claimable: {formatAmount(schedule.claimableAmount)} {selectedToken.symbol}
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
                            Ends: {formatDate(schedule.endTime)}
                          </div>
                          {canClaim && (
                            <VestButton 
                              onClick={() => handleClaim(schedule.vestingId)} 
                              disabled={isClaiming}
                              style={{ fontSize: '12px', padding: '6px 12px', minHeight: 'auto' }}
                            >
                              {isClaiming && claimingId === schedule.vestingId ? 'Claiming...' : 'Claim'}
                            </VestButton>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {error && <ErrorMessage>{error}</ErrorMessage>}
              {success && txHash && (
                <SuccessMessage>
                  Vesting schedule created!
                  <TxLink href={`https://explorer.inkonchain.com/tx/${txHash}`} target="_blank" rel="noopener noreferrer">
                    View on Explorer →
                  </TxLink>
                </SuccessMessage>
              )}

              {needsApproval && (
                <VestButton onClick={handleApprove} disabled={isApproving || !amount || parseFloat(amount) === 0}>
                  {isApproving ? 'Approving...' : 'Approve Token'}
                </VestButton>
              )}

              {!needsApproval && (
                <VestButton onClick={handleVest} disabled={!canVest || isVesting || !!success}>
                  {isVesting ? 'Creating Vesting Schedule...' : success ? 'Vested Successfully' : `Vest ${selectedToken.symbol}`}
                </VestButton>
              )}
            </>
          )}
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
};

export default QuickVestModal;

